import { PageQuery } from '@utils/ApiClient'

import type { DxtradeWatchlistsQuery } from '@model/DxTrade.types'

const sortObjectKeys = (obj: any): any => {
  if (obj === null || typeof obj !== 'object') return obj
  if (Array.isArray(obj)) return obj.map(sortObjectKeys)

  return Object.keys(obj)
    .sort()
    .reduce((result, key) => {
      result[key] = sortObjectKeys(obj[key])
      return result
    }, {} as any)
}

const normalizePageQuery = (query?: PageQuery): string | null => {
  if (!query) return null

  const normalized = {
    search: query.search ?? null,
    sort: query.sort ?? null,
    pageIndex: query.pageIndex ?? null,
    pageSize: query.pageSize ?? null,
  }

  return JSON.stringify(sortObjectKeys(normalized))
}

export const queryKeys = {
  account: {
    root: () => ['account'] as const,
    profile: (locale?: string) => ['account', 'profile', locale ?? null] as const,
    hubspotToken: (accountId?: string, sessionId?: string) =>
      ['account', 'hubspotToken', accountId ?? null, sessionId ?? null] as const,
  },
  wallet: {
    root: () => ['wallet'] as const,
    list: (query?: PageQuery) => ['wallet', 'list', normalizePageQuery(query)] as const,
    byId: (id?: string) => ['wallet', 'byId', id ?? null] as const,
    byType: (walletTypeId?: number) => ['wallet', 'byType', walletTypeId ?? null] as const,
    withTransactions: (accountId?: string, locale?: string) =>
      ['wallet', 'withTransactions', accountId ?? null, locale ?? null] as const,
  },
  transaction: {
    root: () => ['transaction'] as const,
    list: (query?: PageQuery) => ['transaction', 'list', normalizePageQuery(query)] as const,
    byId: (id?: string) => ['transaction', 'byId', id ?? null] as const,
    statusHistory: (id?: string, locale?: string) =>
      ['transaction', 'statusHistory', id ?? null, locale ?? null] as const,
  },
  notification: {
    root: () => ['notification'] as const,
    list: (query?: Record<string, unknown>) =>
      ['notification', 'list', query ? JSON.stringify(sortObjectKeys(query)) : null] as const,
    unread: () => ['notification', 'unread'] as const,
    byId: (notificationId?: string, locale?: string, clientNotificationId?: string) =>
      [
        'notification',
        'byId',
        notificationId ?? null,
        locale ?? null,
        clientNotificationId ?? null,
      ] as const,
  },
  ib: {
    root: () => ['ib'] as const,
    reports: (query?: PageQuery) => ['ib', 'reports', normalizePageQuery(query)] as const,
    campaigns: () => ['ib', 'campaigns'] as const,
    loyalty: (ibId?: string, locale?: string) =>
      ['ib', 'loyalty', ibId ?? null, locale ?? null] as const,
    performance: (ibId?: string, from?: string, to?: string) =>
      ['ib', 'performance', ibId ?? null, from ?? null, to ?? null] as const,
    cjm: (ibId?: string, from?: string, to?: string) =>
      ['ib', 'cjm', ibId ?? null, from ?? null, to ?? null] as const,
    multiLevelCommission: (ibId?: string, isPaused?: boolean) =>
      ['ib', 'multiLevelCommission', ibId ?? null, isPaused ?? null] as const,
    payoutScheme: (ibId?: string) => ['ib', 'payoutScheme', ibId ?? null] as const,
    details: (ibId?: string, locale?: string) =>
      ['ib', 'details', ibId ?? null, locale ?? null] as const,
    rebates: {
      transactions: (ibId?: string, query?: PageQuery) =>
        ['ib', 'rebates', 'transactions', ibId ?? null, normalizePageQuery(query)] as const,
      clients: (ibId?: string, query?: PageQuery) =>
        ['ib', 'rebates', 'clients', ibId ?? null, normalizePageQuery(query)] as const,
      clientsSummary: (ibId?: string, search?: Record<string, unknown>) =>
        ['ib', 'rebates', 'clientsSummary', ibId ?? null, search ?? null] as const,
      approvals: (ibId?: string, query?: PageQuery) =>
        ['ib', 'rebates', 'approvals', ibId ?? null, normalizePageQuery(query)] as const,
      approvalsSummary: (ibId?: string, search?: Record<string, unknown>) =>
        ['ib', 'rebates', 'approvalsSummary', ibId ?? null, search ?? null] as const,
      groups: (ibId?: string, query?: PageQuery) =>
        ['ib', 'rebates', 'groups', ibId ?? null, normalizePageQuery(query)] as const,
      groupsSearch: (ibId?: string, query?: PageQuery) =>
        ['ib', 'rebates', 'groupsSearch', ibId ?? null, normalizePageQuery(query)] as const,
      transactionsSummary: (ibId?: string, search?: Record<string, unknown>) =>
        ['ib', 'rebates', 'transactionsSummary', ibId ?? null, search ?? null] as const,
      tradingAccountsSearch: (ibId?: string, query?: PageQuery) =>
        [
          'ib',
          'rebates',
          'tradingAccountsSearch',
          ibId ?? null,
          normalizePageQuery(query),
        ] as const,
      filterOptions: (ibId?: string) => ['ib', 'rebates', 'filterOptions', ibId ?? null] as const,
      clientsFilterOptions: (ibId?: string) =>
        ['ib', 'rebates', 'clientsFilterOptions', ibId ?? null] as const,
      approvalsFilterOptions: (ibId?: string) =>
        ['ib', 'rebates', 'approvalsFilterOptions', ibId ?? null] as const,
    },
    contest: (payload?: Record<string, unknown>) => ['ib', 'contest', payload ?? null] as const,
    contestResults: (ibId?: string, campaignId?: string, search?: Record<string, unknown>) =>
      ['ib', 'contestResults', ibId ?? null, campaignId ?? null, search ?? null] as const,
    referralMaterials: (locale?: string, search?: Record<string, unknown>) =>
      ['ib', 'referralMaterials', locale ?? null, search ?? null] as const,
    referralMaterialFilterOptions: (locale?: string) =>
      ['ib', 'referralMaterialFilterOptions', locale ?? null] as const,
    transactionHistory: (search?: Record<string, unknown>, locale?: string) =>
      ['ib', 'transactionHistory', search ?? null, locale ?? null] as const,
    income: (search?: Record<string, unknown>, locale?: string) =>
      ['ib', 'income', search ?? null, locale ?? null] as const,
    clientsPerAccountSummary: (ibId?: string, search?: Record<string, unknown>, locale?: string) =>
      ['ib', 'clientsPerAccountSummary', ibId ?? null, search ?? null, locale ?? null] as const,
    clientJourneyMapData: (ibId?: string, search?: Record<string, unknown>, locale?: string) =>
      ['ib', 'clientJourneyMapData', ibId ?? null, search ?? null, locale ?? null] as const,
    clientsOverallSummary: (ibId?: string, query?: PageQuery) =>
      ['ib', 'clientsOverallSummary', ibId ?? null, normalizePageQuery(query)] as const,
    incomeSummary: (search?: Record<string, unknown>) =>
      ['ib', 'incomeSummary', search ?? null] as const,
    cjmSummary: (ibId?: string, search?: Record<string, unknown>) =>
      ['ib', 'cjmSummary', ibId ?? null, search ?? null] as const,
    commissionPlans: (ibId?: string) => ['ib', 'commissionPlans', ibId ?? null] as const,
    commissionPlansSummary: (ibId?: string) =>
      ['ib', 'commissionPlansSummary', ibId ?? null] as const,
    metrics: (ibId?: string, type?: string, from?: string, to?: string) =>
      ['ib', 'metrics', ibId ?? null, type ?? null, from ?? null, to ?? null] as const,
    legalDocuments: (ibId?: string) => ['ib', 'legalDocuments', ibId ?? null] as const,
    subAccounts: (ibId?: string) => ['ib', 'subAccounts', ibId ?? null] as const,
    network: (ibId?: string) => ['ib', 'network', ibId ?? null] as const,
    loyaltyRewardsHistory: (ibId?: string, locale?: string) =>
      ['ib', 'loyaltyRewardsHistory', ibId ?? null, locale ?? null] as const,
    contestCampaigns: (search?: Record<string, unknown>, locale?: string) =>
      ['ib', 'contestCampaigns', search ?? null, locale ?? null] as const,
    contestCampaignResults: (campaignId?: string, search?: Record<string, unknown>) =>
      ['ib', 'contestCampaignResults', campaignId ?? null, search ?? null] as const,
    wallets: () => ['ib', 'wallets'] as const,
    walletTransferList: () => ['ib', 'walletTransferList'] as const,
    signUpTest: (locale?: string, subCategoryId?: string) =>
      ['ib', 'signUpTest', locale ?? null, subCategoryId ?? null] as const,
    signUpLegalDocuments: (locale?: string, subCategoryId?: string) =>
      ['ib', 'signUpLegalDocuments', locale ?? null, subCategoryId ?? null] as const,
    transactionHistoryFilterOptions: (clientWalletType?: string | null, locale?: string) =>
      ['ib', 'transactionHistoryFilterOptions', clientWalletType ?? null, locale ?? null] as const,
    transactionStates: (locale?: string) => ['ib', 'transactionStates', locale ?? null] as const,
    incomeFilterOptions: () => ['ib', 'incomeFilterOptions'] as const,
    clientFilterOptions: (ibId?: string) => ['ib', 'clientFilterOptions', ibId ?? null] as const,
    subAccountPlanOptions: (ibId?: string) =>
      ['ib', 'subAccountPlanOptions', ibId ?? null] as const,
    referralMaterialsUrls: () => ['ib', 'referralMaterialsUrls'] as const,
    referralMaterialsPreviewUrls: () => ['ib', 'referralMaterialsPreviewUrls'] as const,
    qrCode: (params?: Record<string, unknown>) => ['ib', 'qrCode', params ?? null] as const,
    allMetrics: (ibId?: string, from?: string, to?: string, _from?: string, _to?: string) =>
      [
        'ib',
        'allMetrics',
        ibId ?? null,
        from ?? null,
        to ?? null,
        _from ?? null,
        _to ?? null,
      ] as const,
    userContestCampaign: (campaignId?: string) =>
      ['ib', 'userContestCampaign', campaignId ?? null] as const,
    rebateGroupsSummary: (ibId?: string, search?: Record<string, unknown>) =>
      ['ib', 'rebateGroupsSummary', ibId ?? null, search ?? null] as const,
    dashboardLayouts: (locale?: string) => ['ib', 'dashboardLayouts', locale ?? null] as const,
    availableDashboardLayouts: (locale?: string) =>
      ['ib', 'availableDashboardLayouts', locale ?? null] as const,
    referralLinkAliases: (ibId?: string, schemeId?: number) =>
      ['ib', 'referralLinkAliases', ibId ?? null, schemeId ?? null] as const,
    exportHub: {
      all: ['ib', 'exportHub'] as const,
      list: (ibId?: string, search?: Record<string, unknown>, locale?: string) =>
        ['ib', 'exportHub', 'list', ibId ?? null, search ?? null, locale ?? null] as const,
    },
    exportHubFilterOptions: (ibId?: string, locale?: string) =>
      ['ib', 'exportHubFilterOptions', ibId ?? null, locale ?? null] as const,
    exportHubHasUnseen: (ibId?: string) => ['ib', 'exportHub', 'hasUnseen', ibId ?? null] as const,
  },
  trade: {
    root: () => ['trade'] as const,
    instruments: (payload?: Record<string, unknown>) =>
      ['trade', 'instruments', payload ?? null] as const,
    orders: (payload?: Record<string, unknown>) => ['trade', 'orders', payload ?? null] as const,
    dxtrade: {
      accountMetrics: (accountCode: string) =>
        ['trade', 'dxtrade', 'accountMetrics', accountCode] as const,
      instruments: (accountCode: string, symbolsKey: string) =>
        ['trade', 'dxtrade', 'instruments', accountCode, symbolsKey] as const,
      openPositions: (accountCode: string, filters?: Record<string, unknown>) =>
        ['trade', 'dxtrade', 'openPositions', accountCode, filters ?? null] as const,
      orders: (accountCode: string) => ['trade', 'dxtrade', 'orders', accountCode] as const,
      pendingOrders: (accountCode: string, filters?: Record<string, unknown>) =>
        ['trade', 'dxtrade', 'pendingOrders', accountCode, filters ?? null] as const,
      instrumentDetails: (accountCode?: string, symbol?: string | null) =>
        ['trade', 'dxtrade', 'instrumentDetails', accountCode ?? null, symbol ?? null] as const,
      latestCandles: (symbols?: string[], candleTypeId?: string) =>
        ['trade', 'dxtrade', 'latestCandles', symbols ?? null, candleTypeId ?? null] as const,
      publicTradingInfo: (params?: Record<string, unknown>) =>
        ['trade', 'dxtrade', 'publicTradingInfo', params ?? null] as const,
      ordersHistory: (accountCode?: string, query?: Record<string, unknown>) =>
        ['trade', 'dxtrade', 'ordersHistory', accountCode ?? null, query ?? null] as const,
      cashTransfers: (accountCode: string) =>
        ['trade', 'dxtrade', 'cashTransfers', accountCode] as const,
      instrumentsModal: (
        accountCode?: string,
        activeTag?: string | null,
        search?: string,
        favoriteSymbolsKey?: string
      ) =>
        [
          'trade',
          'dxtrade',
          'instrumentsModal',
          accountCode ?? null,
          activeTag ?? null,
          search ?? null,
          favoriteSymbolsKey ?? null,
        ] as const,
      watchlists: (query?: DxtradeWatchlistsQuery) =>
        ['trade', 'dxtrade', 'watchlists', query ?? null] as const,
      sparkline: (symbols?: string[]) =>
        ['trade', 'dxtrade', 'sparkline', symbols ?? null] as const,
      dynamicLeverage: () => ['trade', 'dxtrade', 'dynamicLeverage'] as const,
    },
  },

  //Static / config data (staleTime: Infinity)
  config: {
    supportContacts: (entity: number) => ['config', 'supportContacts', entity] as const,
    publicDomains: (entity: number, host: string) =>
      ['config', 'publicDomains', entity, host] as const,
    lossPercentages: () => ['config', 'lossPercentages'] as const,
    legalDocuments: (locale: string) => ['config', 'legalDocuments', locale] as const,
    entityConfiguration: () => ['config', 'entityConfiguration'] as const,
    customerSupport: (locale: string, companyId: number | null, productId: number | null) =>
      ['config', 'customerSupport', locale, companyId, productId] as const,
    currencyRate: (fromCurrency?: string, toCurrency?: string) =>
      ['config', 'currencyRate', fromCurrency ?? null, toCurrency ?? null] as const,
    passwordValidation: (operationId?: number | string, locale?: string) =>
      ['config', 'passwordValidation', operationId ?? null, locale ?? null] as const,
  },

  //Campaign
  campaign: {
    client: (locale?: string) => ['campaign', 'client', locale ?? null] as const,
    clientList: () => ['campaign', 'clientList'] as const,
    list: (locale?: string) => ['campaign', 'list', locale ?? null] as const,
    rebateDetail: (accountId?: string, campaignId?: string, locale?: string) =>
      ['campaign', 'rebateDetail', accountId ?? null, campaignId ?? null, locale ?? null] as const,
    rebateResults: (accountId?: string, campaignId?: string, search?: Record<string, unknown>) =>
      ['campaign', 'rebateResults', accountId ?? null, campaignId ?? null, search ?? null] as const,
    contestDetail: (accountId?: string, campaignId?: string, locale?: string) =>
      ['campaign', 'contestDetail', accountId ?? null, campaignId ?? null, locale ?? null] as const,
    tradingPoolDetail: (campaignId?: string, locale?: string) =>
      ['campaign', 'tradingPoolDetail', campaignId ?? null, locale ?? null] as const,
    tradingPoolResults: (campaignId?: string) =>
      ['campaign', 'tradingPoolResults', campaignId ?? null] as const,
    tradingPoolDetailAboutModal: (campaignId?: string) =>
      ['campaign', 'tradingPoolDetailAboutModal', campaignId ?? null] as const,
    contestResults: (accountId?: string, campaignId?: string, search?: Record<string, unknown>) =>
      [
        'campaign',
        'contestResults',
        accountId ?? null,
        campaignId ?? null,
        search ?? null,
      ] as const,
    contestDays: (accountId?: string, campaignId?: string) =>
      ['campaign', 'contestDays', accountId ?? null, campaignId ?? null] as const,
    contestDrawdown: (accountId?: string, campaignId?: string) =>
      ['campaign', 'contestDrawdown', accountId ?? null, campaignId ?? null] as const,
    marginBonus: (tradingAccountId?: string, campaignId?: string, amount?: number) =>
      [
        'campaign',
        'marginBonus',
        tradingAccountId ?? null,
        campaignId ?? null,
        amount ?? null,
      ] as const,
  },

  //Stock
  stockDividends: (search?: Record<string, unknown>) => ['stockDividends', search ?? null] as const,

  //Deposit
  deposit: {
    phoneVerified: () => ['deposit', 'phoneVerified'] as const,
    wallets: (walletType?: number) => ['deposit', 'wallets', walletType ?? null] as const,
    tradingAccounts: (isCFD?: boolean) => ['deposit', 'tradingAccounts', isCFD ?? null] as const,
    methods: (target?: string, locale?: string, walletId?: string) =>
      ['deposit', 'methods', target ?? null, locale ?? null, walletId ?? null] as const,
    savedCards: (enabled?: boolean) => ['deposit', 'savedCards', enabled ?? null] as const,
    flowTransactions: (params?: Record<string, unknown>) =>
      ['deposit', 'flowTransactions', params ?? null] as const,
  },

  //Withdrawal
  withdrawal: {
    wallets: (walletType?: number) => ['withdrawal', 'wallets', walletType ?? null] as const,
  },

  //Trading Accounts
  tradingAccount: {
    live: (search?: Record<string, unknown>) => ['tradingAccount', 'live', search ?? null] as const,
    demo: (search?: Record<string, unknown>) => ['tradingAccount', 'demo', search ?? null] as const,
    inactiveLive: (search?: Record<string, unknown>) =>
      ['tradingAccount', 'inactiveLive', search ?? null] as const,
    inactiveDemo: (search?: Record<string, unknown>) =>
      ['tradingAccount', 'inactiveDemo', search ?? null] as const,
    balances: () => ['tradingAccount', 'balances'] as const,
    demoDeposit: (id?: string) => ['tradingAccount', 'demoDeposit', id ?? null] as const,
    totalsAll: () => ['tradingAccount', 'totals'] as const,
    totals: (currency?: string) => ['tradingAccount', 'totals', currency ?? null] as const,
    demoTotals: () => ['tradingAccount', 'demoTotals'] as const,
    configuration: () => ['tradingAccount', 'configuration'] as const,
    platformTypes: (product?: number, ibCode?: string) =>
      ['tradingAccount', 'platformTypes', product ?? null, ibCode ?? null] as const,
    platformTypesAll: () => ['tradingAccount', 'platformTypes'] as const,
    leverageTypes: (platformTypeId?: number) =>
      ['tradingAccount', 'leverageTypes', platformTypeId ?? null] as const,
    ibValidation: (ibCode?: string) => ['tradingAccount', 'ibValidation', ibCode ?? null] as const,
    ibValidationAll: () => ['tradingAccount', 'ibValidation'] as const,
    currencies: (platformTypeId?: number) =>
      ['tradingAccount', 'currencies', platformTypeId ?? null] as const,
    list: (search?: Record<string, unknown>) => ['tradingAccount', 'list', search ?? null] as const,
  },

  //Wallet extended
  walletExtended: {
    totalBalance: (currencyId?: string, walletTypeId?: number) =>
      ['wallet', 'totalBalance', currencyId ?? null, walletTypeId ?? null] as const,
    available: (walletType?: number) => ['wallet', 'available', walletType ?? null] as const,
    activeBonus: (walletId?: string) => ['wallet', 'activeBonus', walletId ?? null] as const,
    depositMethods: (locale?: string, walletId?: string) =>
      ['wallet', 'depositMethods', locale ?? null, walletId ?? null] as const,
    withdrawalMethods: (locale?: string, walletId?: string) =>
      ['wallet', 'withdrawalMethods', locale ?? null, walletId ?? null] as const,
    transferData: (params?: Record<string, unknown>) =>
      ['wallet', 'transferData', params ?? null] as const,
    walletTypes: () => ['wallet', 'walletTypes'] as const,
  },

  //Transactions
  transactionHistory: (search?: Record<string, unknown>, locale?: string) =>
    [
      'transactionHistory',
      search ? JSON.stringify(sortObjectKeys(search)) : null,
      locale ?? null,
    ] as const,
  transactionHistoryFilterOptions: (clientWalletType?: string | null, locale?: string) =>
    ['transactionHistory', 'filterOptions', clientWalletType ?? null, locale ?? null] as const,
  transactionPageData: (params?: Record<string, unknown>) =>
    ['transaction', 'pageData', params ?? null] as const,

  //Subscriptions
  subscriptionHistory: (search?: Record<string, unknown>) =>
    ['subscriptionHistory', search ?? null] as const,
  subscription: {
    statusHistory: (id?: string, locale?: string) =>
      ['subscription', 'statusHistory', id ?? null, locale ?? null] as const,
    bundles: () => ['subscription', 'bundles'] as const,
    group: () => ['subscription', 'group'] as const,
    tradingPlatforms: (params?: Record<string, unknown>) =>
      ['subscription', 'tradingPlatforms', params ?? null] as const,
  },

  //Statements
  statement: {
    availableDates: (taId?: string, statementType?: number) =>
      ['statement', 'availableDates', taId ?? null, statementType ?? null] as const,
    tradingAccount: (search?: Record<string, unknown>) =>
      ['statement', 'tradingAccount', search ?? null] as const,
  },

  //Tools / Platforms
  platforms: {
    info: (locale?: string) => ['platforms', 'info', locale ?? null] as const,
    tradingViewVideos: (locale?: string) =>
      ['platforms', 'tradingViewVideos', locale ?? null] as const,
    learnMore: (platformTypeId?: number, locale?: string) =>
      ['platforms', 'learnMore', platformTypeId ?? null, locale ?? null] as const,
  },

  //Product Registration
  productRegistration: {
    legalDocuments: (product?: number, locale?: string) =>
      ['productRegistration', 'legalDocuments', product ?? null, locale ?? null] as const,
    marketDataClassifications: (locale?: string) =>
      ['productRegistration', 'marketDataClassifications', locale ?? null] as const,
    appTest: (locale?: string, product?: number) =>
      ['productRegistration', 'appTest', locale ?? null, product ?? null] as const,
    appTestStatus: (locale?: string, product?: number) =>
      ['productRegistration', 'appTestStatus', locale ?? null, product ?? null] as const,
    economicProfileTest: (locale?: string, product?: number) =>
      ['productRegistration', 'economicProfileTest', locale ?? null, product ?? null] as const,
    account: (locale?: string) => ['productRegistration', 'account', locale ?? null] as const,
  },

  //Signup
  signup: {
    financialInfoTest: (locale?: string) =>
      ['signup', 'financialInfoTest', locale ?? null] as const,
    economicProfileTest: (locale?: string) =>
      ['signup', 'economicProfileTest', locale ?? null] as const,
    documentTypes: (categoryType?: number, locale?: string) =>
      ['signup', 'documentTypes', categoryType ?? null, locale ?? null] as const,
    nciPriorities: (locale?: string) => ['signup', 'nciPriorities', locale ?? null] as const,
    nciDocumentTypes: (locale?: string) => ['signup', 'nciDocumentTypes', locale ?? null] as const,
    companyCountries: () => ['signup', 'companyCountries'] as const,
    companyTypes: () => ['signup', 'companyTypes'] as const,
    ownershipStructures: () => ['signup', 'ownershipStructures'] as const,
    companyLegalBases: () => ['signup', 'companyLegalBases'] as const,
    emirTestQuestions: (locale?: string) =>
      ['signup', 'emirTestQuestions', locale ?? null] as const,
    nationalities: () => ['signup', 'nationalities'] as const,
    countries: (params?: Record<string, unknown>) =>
      ['signup', 'countries', params ?? null] as const,
    addressAutocomplete: (input?: string, regionCode?: string) =>
      ['signup', 'addressAutocomplete', input ?? null, regionCode ?? null] as const,
    legalDocuments: (
      locale?: string,
      tickmillCompanyId?: number,
      tickmillProductIds: number[] = [],
      countryId?: string
    ) =>
      [
        'signup',
        'legalDocuments',
        locale ?? null,
        tickmillCompanyId ?? null,
        [...tickmillProductIds].sort((a, b) => a - b),
        countryId ?? null,
      ] as const,
  },

  //First Deposit
  firstDeposit: {
    tradingAccounts: (search?: Record<string, unknown>) =>
      ['firstDeposit', 'tradingAccounts', search ?? null] as const,
    walletDepositMethods: (locale?: string, walletId?: string) =>
      ['firstDeposit', 'walletDepositMethods', locale ?? null, walletId ?? null] as const,
  },

  //Marketing
  marketing: {
    lead: (params?: Record<string, unknown>) => ['marketing', 'lead', params ?? null] as const,
  },

  //Documents
  document: {
    list: (search?: Record<string, unknown>, locale?: string) =>
      ['document', 'list', search ?? null, locale ?? null] as const,
    required: () => ['document', 'required'] as const,
    categories: (categoryType?: number, locale?: string) =>
      ['document', 'categories', categoryType ?? null, locale ?? null] as const,
    generalStatus: (clientId?: string, locale?: string) =>
      ['document', 'generalStatus', clientId ?? null, locale ?? null] as const,
  },

  //Payment Agent
  paymentAgent: {
    transactionHistory: (search?: Record<string, unknown>, locale?: string) =>
      ['paymentAgent', 'transactionHistory', search ?? null, locale ?? null] as const,
    filterOptions: (locale?: string) => ['paymentAgent', 'filterOptions', locale ?? null] as const,
    wallets: () => ['paymentAgent', 'wallets'] as const,
    info: () => ['paymentAgent', 'info'] as const,
    transferInfo: (walletId?: string, agentId?: string) =>
      ['paymentAgent', 'transferInfo', walletId ?? null, agentId ?? null] as const,
  },

  //Account info
  accountInfo: {
    root: () => ['accountInfo'] as const,
    limits: () => ['accountInfo', 'limits'] as const,
  },

  //Trading Account Status
  tradingAccountStatus: () => ['tradingAccount', 'status'] as const,

  //IB Referral Codes
  ibReferralCodes: (introducingBrokerId?: string) =>
    ['ib', 'referralCodes', introducingBrokerId ?? null] as const,

  //IB Link
  ibLink: (defaultReferralCode?: string, multiTierReferralCode?: string, locale?: string) =>
    [
      'ib',
      'link',
      defaultReferralCode ?? null,
      multiTierReferralCode ?? null,
      locale ?? null,
    ] as const,

  //Regulators
  regulators: (tickmillCompanyId?: number, locale?: string, locationCountry?: string) =>
    [
      'config',
      'regulators',
      tickmillCompanyId ?? null,
      locale ?? null,
      locationCountry ?? null,
    ] as const,
  appTestCountries: () => ['config', 'appTestCountries'] as const,

  //Market Data Events
  marketDataEvents: (symbolsSignature?: string, eventTypes?: unknown[], account?: string) =>
    [
      'trade',
      'marketDataEvents',
      symbolsSignature ?? null,
      eventTypes ?? null,
      account ?? null,
    ] as const,

  //IB Legal Documents
  ibLegalDocuments: (locale?: string, legalDocumentsSchemes?: string) =>
    ['ib', 'legalDocuments', locale ?? null, legalDocumentsSchemes ?? null] as const,

  //Lucky Draw
  luckyDrawHistory: (campaignId?: string, locale?: string) =>
    ['luckyDraw', 'history', campaignId ?? null, locale ?? null] as const,

  //Subscriptions Cart
  subscriptionCartItems: (isTradersRoomVisible?: boolean) =>
    ['subscription', 'cartItems', isTradersRoomVisible ?? null] as const,
  subscriptionActive: (isTradersRoomVisible?: boolean) =>
    ['subscription', 'active', isTradersRoomVisible ?? null] as const,
  subscriptionPendingUnsubscriptions: (
    isTradersRoomVisible?: boolean,
    subscriptionsDetailId?: string
  ) =>
    [
      'subscription',
      'pendingUnsubscriptions',
      isTradersRoomVisible ?? null,
      subscriptionsDetailId ?? null,
    ] as const,
  subscriptionAvailableToWithdraw: () => ['subscription', 'availableToWithdraw'] as const,

  //Personal Info
  personalInfoAppTestAnswers: (locale?: string) =>
    ['personalInfo', 'appTestAnswers', locale ?? null] as const,
  economicProfileAnswers: (locale?: string) =>
    ['personalInfo', 'economicProfileAnswers', locale ?? null] as const,

  //Appropriateness Test
  accountAppTest: (locale?: string, product?: number) =>
    ['accountAppTest', locale ?? null, product ?? null] as const,

  //Lead Products
  leadProducts: (leadId?: string, leadToken?: string) =>
    ['lead', 'products', leadId ?? null, leadToken ?? null] as const,

  //KYC / Sumsub
  kyc: {
    sumsubToken: () => ['kyc', 'sumsubToken'] as const,
    sumsubStatus: () => ['kyc', 'sumsubStatus'] as const,
  },

  //Profile
  profile: {
    yearlyKycUpdate: (locale?: string) => ['profile', 'yearlyKycUpdate', locale ?? null] as const,
    phoneNumberValidation: (phoneNumber: string) =>
      ['profile', 'phoneNumberValidation', phoneNumber] as const,
    taxHistory: (locale?: string) => ['profile', 'taxHistory', locale ?? null] as const,
    taxFormQuestions: (subcategoryId?: number, locale?: string) =>
      ['profile', 'taxFormQuestions', subcategoryId ?? null, locale ?? null] as const,
    taxFormAnswers: (subcategoryId?: number, locale?: string) =>
      ['profile', 'taxFormAnswers', subcategoryId ?? null, locale ?? null] as const,
    countries: () => ['profile', 'countries'] as const,
    wallets: () => ['profile', 'wallets'] as const,
    economicProfileTest: (locale?: string, product?: number) =>
      ['profile', 'economicProfileTest', locale ?? null, product ?? null] as const,
    nciData: (locale?: string) => ['profile', 'nciData', locale ?? null] as const,
    nciPriorities: () => ['profile', 'nciPriorities'] as const,
    bankAccounts: () => ['profile', 'bankAccounts'] as const,
    bankAccountValidationOptions: () => ['profile', 'bankAccountValidationOptions'] as const,
    classificationTestAnswers: (subcategoryId?: number, locale?: string) =>
      ['profile', 'classificationTestAnswers', subcategoryId ?? null, locale ?? null] as const,
    classificationTest: (subcategoryId?: number, locale?: string) =>
      ['profile', 'classificationTest', subcategoryId ?? null, locale ?? null] as const,
    classificationTestRetake: (subcategoryId?: number) =>
      ['profile', 'classificationTestRetake', subcategoryId ?? null] as const,
    legalDocuments: (locale?: string) => ['profile', 'legalDocuments', locale ?? null] as const,
    appropriatenessTestData: (locale?: string, product?: number) =>
      ['profile', 'appropriatenessTestData', locale ?? null, product ?? null] as const,
    twoFAProviderTypes: () => ['profile', 'twoFAProviderTypes'] as const,
    twoFAProviders: () => ['profile', 'twoFAProviders'] as const,
    twoFAProviderActionTypes: () => ['profile', 'twoFAProviderActionTypes'] as const,
    notificationPreferences: () => ['profile', 'notificationPreferences'] as const,
    clientAreaLanguages: () => ['profile', 'clientAreaLanguages'] as const,
    landingPageOptions: (locale?: string) =>
      ['profile', 'landingPageOptions', locale ?? null] as const,
    dateFormats: () => ['profile', 'dateFormats'] as const,
    displayCurrencies: () => ['profile', 'displayCurrencies'] as const,
  },

  //Rewards hub
  rewardsHub: {
    dashboard: () => ['rewardsHub', 'dashboard'] as const,
    rewardsHistory: () => ['rewardsHub', 'rewardsHistory'] as const,
    campaignsHistory: () => ['rewardsHub', 'campaignsHistory'] as const,
  },

  // Analytics
  analytics: {
    topMoversRisers: (filters: { periodType?: number | null; assetType?: string | null }) =>
      ['analytics', 'topMoversRisers', filters] as const,
    topMoversFallers: (filters: { periodType?: number | null; assetType?: string | null }) =>
      ['analytics', 'topMoversFallers', filters] as const,

    topTradedBought: (filters: { periodType?: number | null; assetType?: string | null }) =>
      ['analytics', 'topTradedBought', filters] as const,
    topTradedSold: (filters: { periodType?: number | null; assetType?: string | null }) =>
      ['analytics', 'topTradedSold', filters] as const,

    mostVolatile: (filters: { periodType?: number | null; assetType?: string | null }) =>
      ['analytics', 'mostVolatile', filters] as const,
    mostFollowed: (filters: { periodType?: number | null; assetType?: string | null }) =>
      ['analytics', 'mostFollowed', filters] as const,
  },
} as const
